import {
  CheckCircleIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { MouseEvent, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { useCopy } from '@/hooks/shared/useCopy';
import { Button } from '@/components/ui/daisy/button';

import { TextWithCopyProps } from './types';

export const TextWithCopy = forwardRef<HTMLDivElement, TextWithCopyProps>(
  ({ text, copyValue, copyLabel, copiedLabel, classNames, ...props }, ref) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { isCopied, handleCopy } = useCopy();

    const label = isCopied
      ? copiedLabel ?? t('copied')
      : copyLabel ?? t('copy');

    const onClick = (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      handleCopy(copyValue ?? text);
    };

    return (
      <div
        ref={ref}
        className={twMerge('flex items-center gap-3', classNames?.base)}
        {...props}
      >
        <Button
          type="button"
          variant="ghost"
          size="sm"
          shape="square"
          aria-label={label}
          className={twMerge('shrink-0 px-0', classNames?.button)}
          onClick={onClick}
        >
          {isCopied ? (
            <CheckCircleIcon className="size-6 text-success" />
          ) : (
            <DocumentDuplicateIcon className="size-6" />
          )}
        </Button>

        <span
          className={twMerge(
            'break-all text-sm text-base-content',
            classNames?.text,
          )}
        >
          {text}
        </span>
      </div>
    );
  },
);

TextWithCopy.displayName = 'TextWithCopy';
